// src/pages/time/LeaveEntitlementPage.jsx
// Extracted from App.js - Leave Entitlement admin page
// BRIO Migration: Using code "4a" (table dense)
import React, { useState } from 'react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  getTablePropsFromCode
} from '../../components/BrioComponents';

// Presentation code for this page
const PRESENTATION_CODE = '4a';

export default function LeaveEntitlementPage({
  // Data
  activeUsers,
  selectedLeaveYear,
  employeeLeaveEntitlement,
  form,
  filters,
  // State setters
  setForm,
  setFilters,
  // Functions - Leave
  recalculateAllEntitlements,
  getEmployeeEntitlement,
  setManualLeaveOverride,
  removeManualLeaveOverride,
  // Functions - Notice
  getEmployeeNoticeEntitlement,
  setManualNoticeOverride,
  removeManualNoticeOverride,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // Local fallback filter state
  const [localFilters, setLocalFilters] = useState({ employmentStatus: 'active' });

  // Safe effective filters and setter
  const hasExternalFilters = typeof setFilters === 'function';
  const effectiveFilters = (filters && typeof filters === 'object') ? filters : localFilters;
  const effectiveSetFilters = hasExternalFilters ? setFilters : setLocalFilters;
  const employmentStatus = effectiveFilters.employmentStatus || 'active';

  // Filter users based on employment status
  const filteredUsers = activeUsers.filter(u => {
    if (employmentStatus === 'active') return u.isActive !== false;
    if (employmentStatus === 'inactive') return u.isActive === false;
    return true;
  });

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

              <p className="brio-analytics-muted brio-mt-xs">Individual leave allocations for {selectedLeaveYear}</p>
            </div>
            <div className="brio-page-header__right">
              <div className="brio-flex brio-items-center brio-gap-xs">
                <button
                  onClick={() => effectiveSetFilters({
                    ...effectiveFilters,
                    employmentStatus: employmentStatus === 'active' ? 'inactive' : 'active'
                  })}
                  className={`brio-btn ${employmentStatus === 'active' ? 'brio-btn-primary' : 'brio-btn-secondary'}`}
                  title="Toggle active / inactive"
                >
                  {employmentStatus === 'active' ? 'Active' : 'Inactive'}
                </button>
                <button 
                  onClick={() => recalculateAllEntitlements(selectedLeaveYear)}
                  className="brio-btn brio-btn-primary"
                >
                  Recalculate All
                </button>
              </div>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'employee';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Employee {form.leaveEntitlementSort?.key === 'employee' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'role';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Role {form.leaveEntitlementSort?.key === 'role' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'years';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Years {form.leaveEntitlementSort?.key === 'years' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'base';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Base {form.leaveEntitlementSort?.key === 'base' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'roleBonus';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Role Bonus {form.leaveEntitlementSort?.key === 'roleBonus' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'tenure';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Tenure {form.leaveEntitlementSort?.key === 'tenure' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'calculated';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Calculated {form.leaveEntitlementSort?.key === 'calculated' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const sortKey = 'final';
                        const currentSort = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                        const newDirection = currentSort.key === sortKey && currentSort.direction === 'asc' ? 'desc' : 'asc';
                        setForm({...form, leaveEntitlementSort: { key: sortKey, direction: newDirection }});
                      }}
                    >
                      Final Total {form.leaveEntitlementSort?.key === 'final' && (form.leaveEntitlementSort?.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Create array with entitlement data for sorting
                    const usersWithEntitlement = filteredUsers.map(user => {
                      const ent = getEmployeeEntitlement(user.id, selectedLeaveYear);
                      const isOverridden = ent.manualOverride !== null;
                      return {
                        user,
                        ent,
                        isOverridden,
                        tenure: ent.yearsEmployed * ent.tenureIncrementPerYear
                      };
                    });
                    
                    // Sort based on current sort settings
                    const sortConfig = form.leaveEntitlementSort || { key: 'employee', direction: 'asc' };
                    const sortedUsers = [...usersWithEntitlement].sort((a, b) => {
                      let valA, valB;
                      
                      switch(sortConfig.key) {
                        case 'employee':
                          valA = a.user.name.toLowerCase();
                          valB = b.user.name.toLowerCase();
                          return sortConfig.direction === 'asc' 
                            ? valA.localeCompare(valB)
                            : valB.localeCompare(valA);
                            
                        case 'role':
                          valA = (a.user.jobRole || '').toLowerCase();
                          valB = (b.user.jobRole || '').toLowerCase();
                          return sortConfig.direction === 'asc' 
                            ? valA.localeCompare(valB)
                            : valB.localeCompare(valA);
                            
                        case 'years':
                          return sortConfig.direction === 'asc' 
                            ? a.ent.yearsEmployed - b.ent.yearsEmployed
                            : b.ent.yearsEmployed - a.ent.yearsEmployed;
                            
                        case 'base':
                          return sortConfig.direction === 'asc' 
                            ? a.ent.baseLeave - b.ent.baseLeave
                            : b.ent.baseLeave - a.ent.baseLeave;
                            
                        case 'roleBonus':
                          return sortConfig.direction === 'asc' 
                            ? a.ent.roleAdditionalLeave - b.ent.roleAdditionalLeave
                            : b.ent.roleAdditionalLeave - a.ent.roleAdditionalLeave;
                            
                        case 'tenure':
                          return sortConfig.direction === 'asc' 
                            ? a.tenure - b.tenure
                            : b.tenure - a.tenure;
                            
                        case 'calculated':
                          return sortConfig.direction === 'asc' 
                            ? a.ent.calculatedTotalLeave - b.ent.calculatedTotalLeave
                            : b.ent.calculatedTotalLeave - a.ent.calculatedTotalLeave;
                            
                        case 'final':
                          return sortConfig.direction === 'asc' 
                            ? a.ent.finalTotalLeave - b.ent.finalTotalLeave
                            : b.ent.finalTotalLeave - a.ent.finalTotalLeave;
                            
                        default:
                          return 0;
                      }
                    });
                    
                    return sortedUsers.map(({ user, ent, isOverridden, tenure }) => (
                      <tr
                        key={user.id}
                        className="brio-row--hoverable"
                        style={isOverridden ? { background: 'var(--brio-yellow-light)' } : {}}
                      >
                        <td className="brio-font-medium">{user.name}</td>
                        <td className="brio-text-grey">{user.jobRole}</td>
                        <td className="text-center">{ent.yearsEmployed}</td>
                        <td className="text-center">{ent.baseLeave}</td>
                        <td className="text-center">{ent.roleAdditionalLeave}</td>
                        <td className="text-center">{tenure.toFixed(1)}</td>
                        <td className="text-center brio-font-medium">{ent.calculatedTotalLeave} days</td>
                        <td className="text-center">
                          {isOverridden ? (
                            <span className="brio-font-bold" style={{ color: 'var(--brio-orange)' }}>{ent.finalTotalLeave} days *</span>
                          ) : (
                            <span className="brio-font-medium" style={{ color: 'var(--brio-gold)' }}>
                              {ent.finalTotalLeave} days
                              {ent.calculatedTotalLeave > 28 && (
                                <span className="brio-text-xs" style={{ color: 'var(--brio-orange)', marginLeft: '4px' }}>(capped)</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          {isOverridden ? (
                              <button
                                onClick={() => removeManualLeaveOverride(user.id, selectedLeaveYear)}
                                className="brio-btn brio-btn--sm brio-btn-secondary"
                              >
                                Reset
                              </button>
                          ) : (
                              <button
                                onClick={() => {
                                  const override = prompt(`Enter manual override for ${user.name}:`, ent.calculatedTotalLeave);
                                  if (override !== null && !isNaN(parseFloat(override))) {
                                    setManualLeaveOverride(user.id, selectedLeaveYear, parseFloat(override));
                                  }
                                }}
                                className="brio-btn brio-btn--sm brio-btn-primary"
                              >
                                Override
                              </button>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </BrioTable>
            </BrioByCode>
            {employeeLeaveEntitlement.some(e => e.manualOverride !== null) && (
              <div className="brio-mt-s brio-text-xs" style={{ color: 'var(--brio-orange)' }}>
                * Manual override applied (highlighted in yellow)
              </div>
            )}
          </BrioAnalyticsSurface>

          {/* ================================================================
              NOTICE PERIOD ENTITLEMENT SECTION
              Mirrors the Leave Entitlement table above
              ================================================================ */}
          <BrioAnalyticsSurface padding="m">
            <div className="brio-mb-s">
              <h4 className="brio-analytics-section-title brio-text-xl brio-font-medium">Notice Period Entitlement</h4>
              <p className="brio-analytics-muted brio-text-xs">Notice period per team member in months (capped at 6)</p>
            </div>
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th className="text-center">Years</th>
                    <th className="text-center">Base</th>
                    <th className="text-center">Role Bonus</th>
                    <th className="text-center">Tenure</th>
                    <th className="text-center">Calculated</th>
                    <th className="text-center">Final Total</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Create array with notice entitlement data for display
                    // Uses same filteredUsers and sort as leave entitlement
                    const usersWithNotice = filteredUsers.map(user => {
                      const ent = getEmployeeNoticeEntitlement ? getEmployeeNoticeEntitlement(user.id, selectedLeaveYear) : null;
                      if (!ent) return null;
                      const isOverridden = ent.manualOverride !== null;
                      return {
                        user,
                        ent,
                        isOverridden,
                        tenure: ent.yearsEmployed * ent.tenureIncrementPerYear
                      };
                    }).filter(Boolean);

                    // Sort alphabetically by name (consistent with leave table default)
                    const sortedUsers = [...usersWithNotice].sort((a, b) => 
                      a.user.name.toLowerCase().localeCompare(b.user.name.toLowerCase())
                    );

                    return sortedUsers.map(({ user, ent, isOverridden, tenure }) => (
                      <tr 
                        key={user.id} 
                        className="brio-row--hoverable"
                        style={isOverridden ? { background: 'var(--brio-yellow-light)' } : {}}
                      >
                        <td className="brio-font-medium">{user.name}</td>
                        <td className="brio-text-grey">{user.jobRole}</td>
                        <td className="text-center">{ent.yearsEmployed}</td>
                        <td className="text-center">{ent.baseNotice}</td>
                        <td className="text-center">{ent.roleAdditionalNotice}</td>
                        <td className="text-center">{tenure.toFixed(1)}</td>
                        <td className="text-center brio-font-medium">{ent.calculatedTotalNotice} months</td>
                        <td className="text-center">
                          {isOverridden ? (
                            <span className="brio-font-bold" style={{ color: 'var(--brio-orange)' }}>{ent.finalTotalNotice} months *</span>
                          ) : (
                            <span className="brio-font-medium" style={{ color: 'var(--brio-gold)' }}>
                              {ent.finalTotalNotice} months
                              {ent.calculatedTotalNotice > 6 && (
                                <span className="brio-text-xs" style={{ color: 'var(--brio-orange)', marginLeft: '4px' }}>(capped)</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="text-center">
                          {isOverridden ? (
                              <button
                                onClick={() => removeManualNoticeOverride(user.id, selectedLeaveYear)}
                                className="brio-btn brio-btn--sm brio-btn-secondary"
                              >
                                Reset
                              </button>
                          ) : (
                              <button
                                onClick={() => {
                                  const override = prompt(`Enter notice period override (months) for ${user.name}:`, ent.finalTotalNotice);
                                  if (override !== null && !isNaN(parseFloat(override))) {
                                    setManualNoticeOverride(user.id, selectedLeaveYear, parseFloat(override));
                                  }
                                }}
                                className="brio-btn brio-btn--sm brio-btn-primary"
                              >
                                Override
                              </button>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </BrioTable>
            </BrioByCode>
            {employeeLeaveEntitlement.some(e => e.noticeOverride !== null && e.noticeOverride !== undefined) && (
              <div className="brio-mt-s brio-text-xs" style={{ color: 'var(--brio-orange)' }}>
                * Manual override applied (highlighted in yellow)
              </div>
            )}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}
